import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Pallets } from '../../../interfaces/Pallets.interfaces';
import { Dealer } from '../../../interfaces/waybill.interfaces';
import { PalletLabelService } from '../../../services/pallet-label.service';
import { NotificationService } from '../../../services/notification.service';
import { ReturnsService } from '../../../services/returns.service';

/** Bolla dei pallet elencati nel popup: codice + pallet che la compongono. */
export interface PrintWaybillSummary {
  waybillCode: string;
  pallets: Pallets[];
}

/**
 * Popup di stampa del reso della pagina /returns: stessa presentazione del
 * return-request-print-popup di return-request (elenco pallet, elenco bolle
 * e due card affiancate per la stampa della label e della bolla).
 * La stampa della label genera il PDF client-side via PalletLabelService;
 * la stampa della bolla chiede il PDF al backend (POST /pallets/waybills-print).
 */
@Component({
  selector: 'app-return-print-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './return-print-popup.component.html',
  styleUrl: './return-print-popup.component.css'
})
export class ReturnPrintPopupComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  /** Reso selezionato in tabella (riga da cui è stato aperto il popup). */
  @Input() set request(value: Pallets | null) {
    this.selectedRequest = value;
    this.refreshLists();
  }
  get request(): Pallets | null {
    return this.selectedRequest;
  }
  /**
   * Pallet della stessa bolla del reso selezionato: se non valorizzato viene
   * elencato il solo reso selezionato.
   */
  @Input() set relatedPallets(value: Pallets[] | null) {
    this.related = (value || []).filter((p) => !!p);
    this.refreshLists();
  }
  /** Anagrafica del dealer mittente del reso (per i dati della label). */
  @Input() dealer: Dealer | null = null;
  @Output() closed = new EventEmitter<void>();

  /** True mentre il PDF delle label è in generazione (disabilita il bottone). */
  isGeneratingLabel = false;
  /** True mentre il backend genera il PDF della bolla. */
  isGeneratingWaybills = false;

  /** Pallet elencati nel popup (stessa presentazione del riepilogo). */
  pallets: Pallets[] = [];
  /** Bolle dei pallet elencati: distinct di palletWaybillCode. */
  waybills: PrintWaybillSummary[] = [];

  private selectedRequest: Pallets | null = null;
  private related: Pallets[] = [];

  constructor(
    private palletLabelService: PalletLabelService,
    private notificationService: NotificationService,
    private returnsService: ReturnsService
  ) {}

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  close(): void {
    this.closed.emit();
  }

  trackByPalletCode(index: number, pallet: Pallets): string {
    return pallet.palletCode || String(index);
  }

  trackByWaybillCode(index: number, waybill: PrintWaybillSummary): string {
    return waybill.waybillCode || String(index);
  }

  /** True per la riga del reso da cui è stato aperto il popup. */
  isSelectedPallet(pallet: Pallets): boolean {
    return !!this.selectedRequest?.palletCode && pallet.palletCode === this.selectedRequest.palletCode;
  }

  /**
   * Ricostruisce gli elenchi mostrati: i pallet della stessa bolla (o il solo
   * reso selezionato) e le bolle che li raggruppano.
   */
  private refreshLists(): void {
    const list = this.related.length
      ? this.related
      : (this.selectedRequest ? [this.selectedRequest] : []);
    this.pallets = list;
    this.waybills = this.groupByWaybill(list);
  }

  /** Raggruppa i pallet elencati per codice bolla mantenendo l'ordine. */
  private groupByWaybill(pallets: Pallets[]): PrintWaybillSummary[] {
    const byCode = new Map<string, PrintWaybillSummary>();
    pallets.forEach((pallet) => {
      const code = (pallet.palletWaybillCode || '').trim();
      if (!code) {
        return;
      }
      const existing = byCode.get(code);
      if (existing) {
        existing.pallets.push(pallet);
        return;
      }
      byCode.set(code, { waybillCode: code, pallets: [pallet] });
    });
    return Array.from(byCode.values());
  }

  /**
   * Genera e scarica il PDF con le label dei pallet elencati (una pagina
   * ciascuno), via PalletLabelService: stessa label di return-request.
   */
  printLabel(): void {
    const printable = this.pallets.filter((p) => !!p.palletCode);
    if (!printable.length) {
      this.notificationService.error('Codice reso mancante: impossibile generare la label');
      return;
    }

    this.isGeneratingLabel = true;
    this.palletLabelService.generatePalletLabels(printable, this.dealer).subscribe({
      next: (success) => {
        this.isGeneratingLabel = false;
        if (success) {
          this.notificationService.success(printable.length === 1
            ? `Label generata per il reso ${printable[0].palletCode}`
            : `${printable.length} label generate`);
        } else {
          this.notificationService.error('Errore nella generazione della label');
        }
      },
      error: (err) => {
        console.error('Errore generazione label pallet:', err);
        this.isGeneratingLabel = false;
        this.notificationService.error('Errore nella generazione della label');
      }
    });
  }

  /**
   * Stampa delle bolle elencate: il backend restituisce un unico PDF con
   * tutte le bolle richieste (POST /pallets/waybills-print).
   */
  printWaybill(): void {
    const codes = this.waybills.map((w) => w.waybillCode).filter((code) => !!code);
    if (!codes.length) {
      this.notificationService.error('Codice bolla mancante: impossibile generare la bolla');
      return;
    }

    this.isGeneratingWaybills = true;
    this.returnsService.printWaybills(codes).subscribe({
      next: (pdf: Blob) => {
        this.isGeneratingWaybills = false;
        this.downloadPdf(pdf, codes);
        this.notificationService.success(codes.length === 1
          ? `Bolla ${codes[0]} generata`
          : `${codes.length} bolle generate`);
      },
      error: (err) => {
        console.error('Errore stampa bolle di reso:', err);
        this.isGeneratingWaybills = false;
        this.notificationService.error(err?.status === 404
          ? 'Nessuna bolla trovata per i codici indicati'
          : 'Errore nella generazione della bolla');
      }
    });
  }

  /** Scarica il PDF restituito dal backend creando un link temporaneo. */
  private downloadPdf(pdf: Blob, codes: string[]): void {
    const url = window.URL.createObjectURL(pdf);
    const link = document.createElement('a');
    link.href = url;
    link.download = codes.length === 1
      ? `Bolla_${codes[0]}.pdf`
      : `Bolle_${codes.length}_resi.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
